/**
 * <klaviyo-back-in-stock>
 * Formulaire d'alerte "retour en stock" branché sur l'API client Klaviyo
 * (Back in Stock). Aucune clé privée n'est exposée : seul l'ID public du
 * compte (company_id) est utilisé, comme prévu pour un appel navigateur.
 *
 * Le composant est ré-instancié à chaque changement de variante par le thème
 * (attribut data-dynamic-variant-content), il n'a donc qu'à gérer l'envoi.
 */
if (!customElements.get('klaviyo-back-in-stock')) {
  class KlaviyoBackInStock extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this.form.dataset.klBound === 'true') return;
      this.form.dataset.klBound = 'true';

      this.input = this.querySelector('[data-kl-email]');
      this.button = this.querySelector('[data-kl-submit]');
      this.buttonLabel = this.button ? this.button.querySelector('[data-kl-submit-label]') : null;
      this.feedback = this.querySelector('[data-kl-feedback]');
      this.formWrap = this.querySelector('[data-kl-form-wrap]');

      this.companyId = this.dataset.companyId;
      this.variantId = this.dataset.variantId;
      this.revision = this.dataset.revision || '2024-10-15';

      this.strings = {
        loading: this.dataset.textLoading || 'Envoi…',
        success: this.dataset.textSuccess || 'Merci ! Vous serez averti(e) par e-mail dès le retour en stock.',
        invalid: this.dataset.textInvalid || 'Merci d’indiquer une adresse e-mail valide.',
        error: this.dataset.textError || 'Une erreur est survenue. Merci de réessayer dans un instant.',
        rateLimit: this.dataset.textRateLimit || 'Trop de tentatives. Merci de patienter quelques instants.'
      };

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    handleSubmit(evt) {
      evt.preventDefault();
      if (this.loading) return;

      const email = (this.input.value || '').trim();
      if (!this.isValidEmail(email)) {
        this.showFeedback(this.strings.invalid, 'error');
        this.input.focus();
        return;
      }

      if (!this.companyId || !this.variantId) {
        this.showFeedback(this.strings.error, 'error');
        return;
      }

      this.setLoading(true);
      this.clearFeedback();
      this.subscribe(email);
    }

    subscribe(email) {
      const url = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.companyId)}`;
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${this.variantId}`
              }
            }
          }
        }
      };

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: this.revision
        },
        body: JSON.stringify(payload)
      })
        .then((response) => {
          if (response.ok) {
            this.onSuccess(email);
            return;
          }
          if (response.status === 429) {
            this.setLoading(false);
            this.showFeedback(this.strings.rateLimit, 'error');
            return;
          }
          this.setLoading(false);
          this.showFeedback(this.strings.error, 'error');
        })
        .catch(() => {
          this.setLoading(false);
          this.showFeedback(this.strings.error, 'error');
        });
    }

    onSuccess(email) {
      // Lie le profil au visiteur suivi si Klaviyo onsite est présent.
      try {
        if (window.klaviyo && typeof window.klaviyo.push === 'function') {
          window.klaviyo.push(['identify', { $email: email }]);
        }
      } catch (e) {
        /* non bloquant */
      }

      this.setLoading(false);
      if (this.formWrap) this.formWrap.hidden = true;
      this.showFeedback(this.strings.success, 'success');
      this.classList.add('kl-bis--done');
    }

    setLoading(state) {
      this.loading = state;
      if (!this.button) return;
      this.button.disabled = state;
      this.button.classList.toggle('kl-bis__btn--loading', state);
      if (this.buttonLabel) {
        if (state) {
          this.defaultLabel = this.defaultLabel || this.buttonLabel.textContent;
          this.buttonLabel.textContent = this.strings.loading;
        } else if (this.defaultLabel) {
          this.buttonLabel.textContent = this.defaultLabel;
        }
      }
    }

    showFeedback(message, type) {
      if (!this.feedback) return;
      this.feedback.textContent = message;
      this.feedback.hidden = false;
      this.feedback.classList.toggle('kl-bis__feedback--error', type === 'error');
      this.feedback.classList.toggle('kl-bis__feedback--success', type === 'success');
    }

    clearFeedback() {
      if (!this.feedback) return;
      this.feedback.hidden = true;
      this.feedback.textContent = '';
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
  }

  customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
}
